import React, { memo, useEffect, useMemo, useRef } from "react";
import { useImmer } from "use-immer";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  GraphConfig,
} from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { FilterMethodHeaderDropdown } from "@src/features/analytics/baseDashboard/FilterMethodHeaderDropdown";
import { useSetGridWidthAnalytics } from "@src/features/analytics/hooks/use-set-grid-width-analytics";
import { InteractiveChartLegend } from "@src/features/analytics/legends/InteractiveChartLegend";
import { MetricDetailSection } from "@src/features/analytics/metricDetails/MetricDetailSection";
import { MetricDetailsWrapper } from "@src/features/analytics/metricDetails/MetricDetailsWrapper";
import { AnalyticsAudienceTableWrapper } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTableWrapper";
import {
  getDefaultGroupGraphFilterValueTb,
  orderFiltersByValue,
} from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import type { BarMetricData } from "@src/types/analytics";
import { UseQueryResult } from "@tanstack/react-query";

interface ShippingAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}

export const ShippingAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: ShippingAnalyticsPageProps) => {
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");

    const [hover, setHover] = useImmer(-1);

    const formattedGroupedData = useMemo(() => {
      return (selectData({
        dashboardData: data,
        config: graphConfigs[5],
        _resource: experience,
        locale: storeCurrencyConfig?.value?.locale,
        metricFilterIndex: 0,
        variations,
      }) || []) as BarMetricData[];
    }, [data]);

    const [filterMethod, setFilterMethod] = useImmer<string>(
      getDefaultGroupGraphFilterValueTb(formattedGroupedData)
    );

    const [focusGroups, setFocusGroups] = useImmer<string[]>([]);

    const handleLegendClick = (dataKey: string) => {
      if (focusGroups.includes(dataKey)) {
        setFocusGroups(focusGroups.filter((el) => el !== dataKey));
      } else {
        setFocusGroups((prev) => [...prev, dataKey]);
      }
    };

    const ref = useRef<HTMLDivElement | null>(null);
    const gridWidth = useSetGridWidthAnalytics(ref);

    useEffect(() => {
      if (formattedGroupedData) {
        setFilterMethod(
          getDefaultGroupGraphFilterValueTb(formattedGroupedData)
        );
      }
    }, [formattedGroupedData]);

    return (
      <Stack ref={ref} direction="column" rowGap={3} columnGap={3}>
        <Grid>
          <BaseMetricSection
            groupTitle={"Shipping Metrics"}
            showCompareGroups={true}
          >
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(0);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  totalTestVisitors={totalExperienceVisitors}
                  config={graphConfigs[0]}
                  renderLocation={"experiment"}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[0],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(1);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  config={graphConfigs[1]}
                  totalTestVisitors={totalExperienceVisitors}
                  renderLocation={"experiment"}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[1],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
          </BaseMetricSection>
        </Grid>
        <Grid size={12}>
          <BaseMetricSection
            groupTitle={"View by Shipping Method"}
            headerDropdown={
              <FilterMethodHeaderDropdown
                selectedFilterOption={filterMethod}
                setSelectedFilterOption={setFilterMethod}
                filterOptions={
                  orderFiltersByValue(formattedGroupedData).map(
                    (option) => option.filterMethod
                  ) as string[]
                }
              />
            }
          >
            <Grid size={6}>
              <MetricDetailSection
                // tooltip={"asd"}
                title={"Orders by Shipping Method"}
                metricDetails={{
                  isPrimary: false,
                }}
                borderRadius={"6px"}
              >
                <BaseGraphPaper hideBorder={true}>
                  <GraphsWrapper
                    config={graphConfigs[4]}
                    testDuration={testDuration}
                    isDataLoading={data[0].isLoading || data[0].isPending}
                    totalTestVisitors={totalExperienceVisitors}
                    filterMethod={filterMethod}
                    focusGroups={focusGroups}
                    renderLocation={"experiment"}
                    data={selectData({
                      dashboardData: data,
                      config: graphConfigs[4],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    rawData={data}
                    compareVariationId={compareVariationId}
                  />
                </BaseGraphPaper>
              </MetricDetailSection>
            </Grid>
            <Grid size={6}>
              <MetricDetailSection
                title={"Average Shipping Revenue by Method"}
                metricDetails={{
                  isPrimary: false,
                }}
                borderRadius={"6px"}
              >
                <BaseGraphPaper hideBorder={true}>
                  <GraphsWrapper
                    testDuration={testDuration}
                    config={graphConfigs[5]}
                    isDataLoading={data[0].isLoading || data[0].isPending}
                    totalTestVisitors={totalExperienceVisitors}
                    renderLocation={"experiment"}
                    filterMethod={filterMethod}
                    focusGroups={focusGroups}
                    data={selectData({
                      dashboardData: data,
                      config: graphConfigs[5],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    rawData={data}
                    compareVariationId={compareVariationId}
                  />
                </BaseGraphPaper>
              </MetricDetailSection>
            </Grid>
            <Grid size={12}>
              <InteractiveChartLegend
                focusGroups={focusGroups}
                onLegendClick={handleLegendClick}
                lines={variations.map((variation) => {
                  return { id: variation.id, name: variation.name };
                })}
              />
            </Grid>
          </BaseMetricSection>
        </Grid>
        <Grid>
          <MetricDetailsWrapper
            graphConfigs={graphConfigs}
            variationOverviewData={data}
            dropdownOptions={graphConfigs.slice(0, 2).map((config) => ({
              title: config.title[0],
              id: config.metrics[0],
            }))}
          />
        </Grid>
        <Grid>
          <AnalyticsAudienceTableWrapper
            experience={experience}
            variations={variations}
            dashboardData={data}
            graphConfigs={graphConfigs.filter((config) => config.hasAudience)}
            testDuration={testDuration}
          />
        </Grid>
      </Stack>
    );
  }
);
